import { useState, useRef, useEffect, useCallback } from 'react';
import { loadGoogleMaps } from '../utils/googleMaps';

let debounceTimer = null;
let autocompleteService = null;
let geocoderService = null;

async function getServices() {
  await loadGoogleMaps();
  if (!autocompleteService) autocompleteService = new window.google.maps.places.AutocompleteService();
  if (!geocoderService)     geocoderService     = new window.google.maps.Geocoder();
}

export default function AddressAutocomplete({ value, onChange, onSelect, disabled }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen]               = useState(false);
  const [activeIdx, setActiveIdx]     = useState(-1);
  const containerRef                  = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    // Pre-load Google Maps SDK on mount so first keystroke is instant
    getServices().catch(() => {});
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchSuggestions = useCallback((input) => {
    clearTimeout(debounceTimer);
    if (!input || input.trim().length < 3) { setSuggestions([]); setOpen(false); return; }
    debounceTimer = setTimeout(async () => {
      try {
        await getServices();
        autocompleteService.getPlacePredictions(
          { input: input.trim(), types: ['address'], componentRestrictions: { country: 'us' } },
          (preds, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && preds?.length) {
              setSuggestions(preds.slice(0, 6));
              setOpen(true);
            } else {
              setSuggestions([]);
              setOpen(false);
            }
            setActiveIdx(-1);
          }
        );
      } catch {
        setSuggestions([]);
      }
    }, 200);
  }, []);

  const handleInput = (e) => {
    onChange(e.target.value);
    fetchSuggestions(e.target.value);
  };

  const selectSuggestion = async (description) => {
    onChange(description);
    setSuggestions([]);
    setOpen(false);
    try {
      await getServices();
      geocoderService.geocode({ address: description }, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          const loc = results[0].geometry.location;
          if (onSelect) onSelect(description, { lat: loc.lat(), lng: loc.lng(), photoUrl: null });
        } else {
          if (onSelect) onSelect(description, { lat: null, lng: null, photoUrl: null });
        }
      });
    } catch {
      if (onSelect) onSelect(description, { lat: null, lng: null, photoUrl: null });
    }
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIdx >= 0 && suggestions[activeIdx]) {
        selectSuggestion(suggestions[activeIdx].description);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} style={{ flex: 1, position: 'relative' }}>
      <input
        type="text"
        className="input input-lg"
        value={value}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder="Type an address — e.g. 123 Main St, Austin, TX"
        disabled={disabled}
        style={{ width: '100%' }}
        autoComplete="off"
        spellCheck={false}
      />

      {open && suggestions.length > 0 && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
          zIndex: 99999, overflow: 'hidden'
        }}>
          {suggestions.map((s, i) => (
            <div
              key={s.place_id}
              onMouseDown={() => selectSuggestion(s.description)}
              style={{
                padding: '11px 14px', cursor: 'pointer', fontSize: '0.9rem',
                background: i === activeIdx ? 'var(--bg-card-hover)' : 'transparent',
                borderBottom: i < suggestions.length - 1 ? '1px solid var(--border)' : 'none',
                display: 'flex', alignItems: 'center', gap: 10
              }}
              onMouseEnter={() => setActiveIdx(i)}
            >
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--primary)', flexShrink: 0 }} />
              <div>
                <div style={{ color: 'var(--text-primary)', fontWeight: 600, lineHeight: 1.3 }}>
                  {s.structured_formatting?.main_text || s.description.split(',')[0]}
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', marginTop: 2 }}>
                  {s.structured_formatting?.secondary_text || s.description.split(',').slice(1).join(',').trim()}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
